import React from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon.js';
import kali from 'kali';

import ContentGroups from './ContentGroups.js';
import TemplateSkeleton from './TemplateSkeleton.js';
import TemplateBuilder from './TemplateBuilder.js';
import N8nChat from './N8nChat.js';
import {
	updatesAllowed,
} from './systemRules.js';

import style from './SystemProgramming.module.css';
import {
	getSession,
} from 'Vulture/Session.js';
import {
	saveData,
} from 'Vulture/Data.js';
import {
	getFDValue,
	matchFDKeys,
	buildDDPayload,
	parseEntityName,
} from 'Vulture/FormDataHelpers.js';
import {
	ListingConfigContext,
} from './ListingConfigContext.js';

const broncoURL = window._getEnv('BRONCO_URL');

// Step consts
const SETUP = 1;
const ASSIGN = 2;
const GROUP = 3;
const SYSTEMS = 4;

// The hash is user-controlled; malformed percent-encoding makes decodeURIComponent
// throw URIError. Fall back to '' so a bad deep link just disables restore instead
// of crashing the component during construction.
const safeDecodeHashValue = (val) => {
	try {
		return decodeURIComponent(val);
	} catch (err) {
		return '';
	}
};

class SystemProgramming extends React.Component {
	static contextType = ListingConfigContext;

	constructor(props) {
		super(props);

		let initialStep = SETUP;
		const stepMatch = window.location.hash.match(/step=(\d+)/);
		const savedStep = stepMatch ? parseInt(stepMatch[1], 10) : NaN;
		if (savedStep >= SETUP && savedStep <= SYSTEMS) {
			initialStep = savedStep;
		}

		const entityMatch = window.location.hash.match(/entity=([^&]+)/);
		const systemMatch = window.location.hash.match(/system=([^&]+)/);
		this._restoreEntityKey = entityMatch ? safeDecodeHashValue(entityMatch[1]) : '';
		this._restoreSystemUUID = systemMatch ? safeDecodeHashValue(systemMatch[1]) : '';

		this.state = {
			activeAccountUUID:        '',
			activeEntityKey:          '',
			activeSystemUUID:         '',
			activeTLCUUID:            '',
			cb:                       null,
			assignmentMap:            {},
			contentGroupsSubmitForm:  () => { },
			globalTemplateData:       [],
			hasFetchedData:           false,
			pendingUpdates:           {},
			skeletonHasUpdated:       false,
			systemMap:                {},
			systemGroupMap:           {},
			step:                     initialStep,
			showLoader:               false,
			blockUpdatesEmptyColumn:  false,
			blockUpdatesDataConflict: false,
			shouldRebuildBundleMap:   false,
			pendingModalOpen:         false,
			lastSaveTime:             (new Date()).getTime(),
		};
	}

	/**
	 * Fetches all core data needed for form state and downstream checks.
	 *
	 * Rationale:
	 * - Early fetching ensures that form state is fully initialized before any
	 *   downstream logic (such as template in-use checks) is run.
	 * - This avoids subtle bugs where form/orgVals are not ready when needed.
	 * - Systems data is fetched lazily when navigating to the SYSTEMS step to improve initial page load performance.
	 */
	callInitDataFetchers() {
		const { accountUUID } = this.props;
		this.fetchGlobalTemplateData();
		this.fetchAccountTemplates(accountUUID);
		// N8n chat is mounted via the <N8nChat /> component in render() — no
		// imperative bootstrap here. See N8nChat.js for the lifecycle.
		// fetchAccountSystems() is called lazily when navigating to SYSTEMS step (see handleStepChange)
	}

	componentDidMount() {
		this.callInitDataFetchers();

		// Steps ASSIGN and SYSTEMS need account systems data; callInitDataFetchers only loads templates.
		if (this.state.step === ASSIGN || this.state.step === SYSTEMS) {
			this.fetchAccountSystems();
		}

		this.scheduleHashRestoreClick();
		this.syncHashToStep();
	}

	scheduleHashRestoreClick() {
		const { step } = this.state;

		if ((step === SETUP || step === ASSIGN) && this._restoreEntityKey) {
			const entityKey = this._restoreEntityKey;
			this._restoreEntityKey = '';
			this.pollForElement(`tb_template_${entityKey.replace(/^template\./, '')}`, (card) => card.click());
		}

		if (step === SYSTEMS && this._restoreSystemUUID) {
			const systemUUID = this._restoreSystemUUID;
			this._restoreSystemUUID = '';
			this.pollForElement(`sp_system_${systemUUID}`, (row) => row.click());
		}
	}

	/**
	 * Poll for a DOM element by id until it appears, then invoke onFound(el).
	 *
	 * The restored step's data (templates / account systems) is fetched async in
	 * componentDidMount, so the card/row we want to click may not be rendered for
	 * a few hundred ms — and on a slow connection or large account, page load can
	 * run for minutes. A single fixed timeout races that load and silently no-ops
	 * if it loses, so we retry on an interval until the element exists or we hit
	 * TIMEOUT_MS. The deadline is a generous backstop for the genuinely
	 * never-renders case (the saved design/system was deleted or isn't in this
	 * account), not a bet on how fast the page loads — hence 3 minutes.
	 *
	 * Only one restore poll is ever in flight (entity-restore runs on SETUP/ASSIGN,
	 * system-restore on SYSTEMS — mutually exclusive by step), so we track a single
	 * timer id that componentWillUnmount cancels.
	 */
	pollForElement(elementId, onFound) {
		const INTERVAL_MS = 150;
		const TIMEOUT_MS = 180000;
		const deadline = Date.now() + TIMEOUT_MS;

		// Cancel any poll already in flight so we never run two concurrently.
		if (this._restorePollTimer) {
			clearTimeout(this._restorePollTimer);
			this._restorePollTimer = null;
		}

		const tick = () => {
			const el = document.getElementById(elementId);
			if (el) {
				this._restorePollTimer = null;
				onFound(el);
				return;
			}
			if (Date.now() >= deadline) {
				this._restorePollTimer = null;
				return;
			}
			this._restorePollTimer = window.setTimeout(tick, INTERVAL_MS);
		};

		tick();
	}

	componentWillUnmount() {
		if (this._restorePollTimer) {
			clearTimeout(this._restorePollTimer);
			this._restorePollTimer = null;
		}
	}

	syncHashToStep() {
		const { step, activeEntityKey, activeSystemUUID } = this.state;
		let expected = `#step=${step}`;
		if ((step === SETUP || step === ASSIGN) && activeEntityKey) {
			expected += `&entity=${encodeURIComponent(activeEntityKey)}`;
		}
		if (step === SYSTEMS && activeSystemUUID) {
			expected += `&system=${encodeURIComponent(activeSystemUUID)}`;
		}
		if (window.location.hash !== expected) {
			window.history.replaceState(null, '', window.location.pathname + window.location.search + expected);
		}
	}

	/**
	 * Build the metadata payload sent to the n8n chat agent on every message.
	 * The n8n workflow reads `$json.metadata.*` and grounds the LLM with what
	 * the user is actually looking at.
	 *
	 * Two layers of context:
	 *   1. Lightweight references (account/design/system UUIDs, step) — always
	 *      included, cheap to send.
	 *   2. The active-design subtree from form data — included when a design is
	 *      selected AND the serialized payload is under the size cap. Lets the
	 *      agent reason about the actual skeleton/settings without us having to
	 *      stand up a bronco→n8n service-account API path (which would be the
	 *      "right" long-term solution but is a separate piece of work).
	 *
	 * Size cap (`MAX_SKELETON_PAYLOAD_BYTES`) protects against shipping huge
	 * blobs over the chat webhook on every message — n8n cloud can handle
	 * sizable payloads but we don't want to be wasteful, and the LLM context
	 * window has limits. If the design exceeds the cap we set
	 * `skeleton_attached=false` and the agent's system prompt is wired to ask
	 * the user to be more specific in that case.
	 *
	 * NOTE: rebuilt on every render via the `metadata` prop. N8nChat does an
	 * in-place mutation of `chatInstance.options.metadata` when this object's
	 * identity changes, so cheap recomputation is fine.
	 */
	buildN8nMetadata() {
		const { accountUUID, fd } = this.props;
		const {
			activeEntityKey,
			activeTLCUUID,
			activeSystemUUID,
			step,
		} = this.state;

		const stepName = ({
			[SETUP]:   'setup',
			[ASSIGN]:  'assign',
			[GROUP]:   'group',
			[SYSTEMS]: 'systems',
		})[step] || 'unknown';

		// Resolve a friendly design name from FD when we have an entity key.
		let designName;
		if (activeEntityKey && fd && fd[activeEntityKey]) {
			designName = fd[activeEntityKey].display_name || fd[activeEntityKey].name;
		}

		const metadata = {
			account_uuid:        accountUUID || null,
			active_entity_key:   activeEntityKey || null,
			active_tlc_uuid:     activeTLCUUID || null,
			active_system_uuid:  activeSystemUUID || null,
			design_name:         designName || null,
			ui_step:             stepName,
			source:              'system_programming',
			skeleton_attached:   false,
			skeleton_uuid:       null,
			skeleton_json:       null,
		};

		// Walk FD from the active template down to the skeleton.
		// FD is hierarchical: template.<uuid> has a nested template_config.<uuid>
		// reference; template_config.<uuid> has a nested template_skeleton.<uuid>
		// reference. fd[template_skeleton.<uuid>].data is already decoded JSON
		// at runtime (the gzip+base64 form on the wire is unwrapped by upstream
		// FD plumbing — see TemplateBuilder's existing skeleton render path
		// which consumes the same shape). We pass it straight through.
		//
		// Size cap protects against shipping huge blobs over the chat webhook
		// on every message; typical decoded skeletons are 10–80 KB.
		const MAX_SKELETON_PAYLOAD_BYTES = 200 * 1024;
		const findRefUUID = (node, prefix) => {
			if (!node) {
				return null;
			}
			for (const k of Object.keys(node)) {
				if (k.startsWith(prefix)) {
					return k.slice(prefix.length);
				}
			}
			return null;
		};

		if (activeEntityKey && fd && fd[activeEntityKey]) {
			try {
				const template = fd[activeEntityKey];
				const configUUID = findRefUUID(template, 'template_config.');
				if (configUUID) {
					const config = fd[`template_config.${configUUID}`];
					const skelUUID = findRefUUID(config, 'template_skeleton.');
					if (skelUUID) {
						const skel = fd[`template_skeleton.${skelUUID}`];
						const data = skel && skel.data;
						if (typeof data === 'string' && data.length > 0 && data.length <= MAX_SKELETON_PAYLOAD_BYTES) {
							metadata.skeleton_attached = true;
							metadata.skeleton_uuid = skelUUID;
							metadata.skeleton_json = data;
						}
					}
				}
			} catch (err) {
				console.warn('[SystemProgramming] could not extract skeleton for chat metadata', err);
			}
		}

		return metadata;
	}

	componentDidUpdate(prevProps, prevState) {
		const {
			accountUUID,
			fd,
			app,
		} = this.props;

		const {
			activeSystemUUID,
		} = this.state;

		if (prevProps.accountUUID !== accountUUID) {
			this._lastTemplatesFetch = null;
			this._lastGlobalTemplateFetch = null;
			this._lastSystemsFetch = null;

			let nextStep = SETUP;
			const hashMatch = window.location.hash.match(/step=(\d+)/);
			const savedStep = hashMatch ? parseInt(hashMatch[1], 10) : NaN;
			if (savedStep >= SETUP && savedStep <= SYSTEMS) {
				nextStep = savedStep;
			}

			// Cross-account switching does NOT carry entity/system selections —
			// those are account-scoped. Hash gets re-asserted by syncHashToStep
			// to drop any stale entity/system from the previous account.
			this._restoreEntityKey = '';
			this._restoreSystemUUID = '';

			this.setState({
				step:             nextStep,
				activeEntityKey:  '',
				activeSystemUUID: '',
				activeTLCUUID:    '',
			}, () => {
				// When switching accounts, re-fetch all core data to ensure form state is fresh.
				this.callInitDataFetchers();
				if (nextStep === ASSIGN || nextStep === SYSTEMS) {
					this.fetchAccountSystems();
				}
			});

			return;
		}

		if ((JSON.stringify(prevProps.fd) !== JSON.stringify(fd))) {
			if (this.state.step === SYSTEMS && this.state.activeSystemUUID) {
				this.makeMaps();
			} else {
				this.initSystemProgramming();
			}
		}

		if (activeSystemUUID !== prevState.activeSystemUUID && activeSystemUUID !== '') {
			app.fetchAccountSystem(accountUUID, activeSystemUUID);
		}

		this.syncHashToStep();

		if (this.state.skeletonHasUpdated) {
			if (Object.keys(fd).length > 0) {
				this.setState({
					activeAccountUUID:  accountUUID,
					skeletonHasUpdated: false,
				}, () => {
					// return;

					// this.buildSystemMap();
					this.makeMaps();
					// this.findSettings();
				});
			}
		}
		/* this.refreshAccount(); */ //this causes a Maximum Update Depth Exceeded error
	}

	initSystemProgramming() {
		const {
			accountUUID,
			fd,
		} = this.props;

		if (Object.keys(fd).length > 0) {
			this.setState({
				activeAccountUUID: accountUUID,
				assignmentMap:     {},
			}, () => {
				this.makeMaps();
			});
		}
	}

	refreshAccount() {
		const {
			app,
			accountUUID,
		} = this.props;

		this.setState({
			pendingUpdates: {},
		}, () => {
			app.fetchAccountTemplates(accountUUID);
			app.fetchAccountSystems(accountUUID);
		});
	}

	fetchAccountTemplates() {
		const {
			app,
			accountUUID,
		} = this.props;

		this._lastTemplatesFetch = Date.now();
		app.fetchAccountTemplates(accountUUID);
	}

	fetchAccountSystems() {
		const {
			app,
			accountUUID,
		} = this.props;

		this._lastSystemsFetch = Date.now();
		app.fetchAccountSystems(accountUUID);
	}

	fetchAccountSystem(systemUUID) {
		const {
			app,
			accountUUID,
		} = this.props;

		app.fetchAccountSystem(accountUUID, systemUUID);
	}

	setSkeletonHasUpdated() {
		this.setState({
			skeletonHasUpdated: true,
		});
	}

	makeSystemGroupMap() {
		const {
			fd,
		} = this.props;

		const systemGroupMap = {};
		for (const [ m1Key, m1Data, ] of Object.entries(fd)) {
			if (m1Key.startsWith('system_group.')) {
				if (!systemGroupMap[m1Key]) {
					systemGroupMap[m1Key] = {};
				}

				for (const m2Key of Object.keys(m1Data)) {
					if (m2Key.startsWith('system.')) {
						systemGroupMap[m1Key][m2Key] = {};
					}
				}
			}
		}

		return systemGroupMap;
	}

	makeSystemMap() {
		const {
			fd,
		} = this.props;

		const systemMap = {};
		for (const m1Key of Object.keys(fd)) {
			if (m1Key.startsWith('system.')) {
				systemMap[m1Key] = {};
				this.makeComponentMap(fd, systemMap[m1Key], m1Key);
			}
		}

		return systemMap;
	}

	makeComponentMap(formData, systemMap, parentM1Key) {
		for (const m1Key of Object.keys(formData[parentM1Key])) {
			if (m1Key.startsWith('component.') && formData[m1Key]) {
				systemMap[m1Key] = { ...formData[m1Key] };
				systemMap[m1Key]['parent'] = parentM1Key;

				for (const m2Key of Object.keys(formData[m1Key])) {
					if (m2Key.startsWith('component_type.')) {
						systemMap[m1Key]['type'] = formData[m2Key].type;
					}
				}

				this.makeComponentMap(formData, systemMap, m1Key);
			}

			let isListing = false;
			if (m1Key.startsWith('listing_collection.')) {
				isListing = true;
			}
			if (m1Key.startsWith('listing_group.')) {
				isListing = true;

				for (const [ m2Key, m2Data, ] of Object.entries(formData[m1Key])) {
					if (m2Key.startsWith('listing_column_group.')) {
						systemMap[m2Key] = {
							...formData[m2Key],
							...m2Data,
						};
					}
				}
			}

			if (isListing) {
				systemMap[m1Key] = { ...formData[m1Key] };
				systemMap[m1Key]['parent'] = parentM1Key;
			}
		}
	}

	makeMaps(cb = () => { }) {
		const systemGroupMap = this.makeSystemGroupMap();
		const systemMap = this.makeSystemMap();

		this.setState({
			systemGroupMap,
			systemMap,
		}, cb);
	}

	fetchGlobalTemplateData() {
		if (this._globalTemplateDataInflight) return;
		this._globalTemplateDataInflight = true;

		const url = `${broncoURL}/template_category`;
		const sessionKey = getSession().session;

		new kali({
			headers: {
				'content_type':   'application/json',
				'X-Auth-Session': sessionKey,
			},
			method: 'POST',
		}).post(url, {
			success: (_kali, res, data) => {
				this._globalTemplateDataInflight = false;
				this._lastGlobalTemplateFetch = Date.now();
				if (data) {
					this.setState({
						globalTemplateData: data,
					});
				}
			},

			failure: (_kali, res, err) => {
				this._globalTemplateDataInflight = false;
				console.error(err);
			},
		});
	}

	async handleSubmit(modalCallback = () => { }, cbKey, cbVal) {
		const {
			app,
			accountUUID,
		} = this.props;

		const {
			activeSystemUUID,
			cb,
			contentGroupsSubmitForm,
			pendingUpdates,
			step,
			assignmentMap,
			systemMap,
		} = this.state;

		const [ updatesAreAllowed, errorMsg, ] = updatesAllowed(pendingUpdates, systemMap);

		// Should not allow saving if there are forbidden data inputted on the settings
		if (!updatesAreAllowed) {
			alert(errorMsg);
			return;
		}

		if (step === SETUP || step === ASSIGN) {
			// Step SETUP and ASSIGN can require cloning configs when editing global templates
			if (cb) {
				return cb(pendingUpdates)
					.then(async (resolvedUpdates) => {
						const updatesToSave = resolvedUpdates || pendingUpdates;
						const payload = buildDDPayload(updatesToSave);
						if (payload) {
							await saveData(payload);
						}
						this.makeMaps();

						this.setState({
							cb:             null,
							pendingUpdates: {},
						}, () => {
							modalCallback(cbKey, cbVal);
							this.refreshAccount();
						});
					})
					.catch((err) => {
						console.error(err);
						alert('Error saving.');
					});
			}

			// Handle assignmentMap for step ASSIGN (Assign page)
			if (step === ASSIGN && Object.keys(assignmentMap).length > 0) {
				app.setState({
					loading: true,
				});
				app.applySystemTemplateAssignment(accountUUID, assignmentMap)
					.then(async () => {
						// If there are also pendingUpdates, save them
						if (Object.keys(pendingUpdates).length > 0) {
							const payload = buildDDPayload(pendingUpdates);
							if (payload) {
								await saveData(payload);
							}
							this.setState({
								pendingUpdates: {},
							}, () => {
								// Fetch fresh data, then clear assignmentMap after data is loaded
								this.fetchAccountTemplates();
								this.fetchAccountSystems();
								this.setState({
									assignmentMap: {},
								}, () => {
									app.setState({
										loading: false,
									});
								});
							});
						} else {
							// No pendingUpdates, just fetch and clear
							this.fetchAccountTemplates();
							this.fetchAccountSystems();
							this.setState({
								assignmentMap: {},
							}, () => {
								app.setState({
									loading: false,
								});
							});
						}
					});
				return;
			}

			// Standard save without cloning
			const payload = buildDDPayload(pendingUpdates);
			if (payload) {
				await saveData(payload);
			}

			this.setState({
				pendingUpdates: {},
			}, () => {
				this.fetchAccountTemplates();
				// Step 2 (ASSIGN) has accountSystems data loaded, which may have stale
				// template references after a delete. Refresh it to clear those references.
				if (step === ASSIGN) {
					this.fetchAccountSystems();
				}
			});

			return;
		}

		if (step === SYSTEMS) {
			app.setState({
				loading: true,
			});

			let hasVisualContentLoopUpdate = false;
			for (const key of Object.keys(pendingUpdates)) {
				if (key.startsWith('visual_content_loop_')) {
					hasVisualContentLoopUpdate = true;
					break;
				}
			}

			let lastSaveTime = this.state.lastSaveTime;
			if (hasVisualContentLoopUpdate) {
				lastSaveTime = (new Date()).getTime();
			}

			const systemsPayload = buildDDPayload(pendingUpdates);
			if (systemsPayload) {
				await saveData(systemsPayload);
			}
			this.setState({
				pendingUpdates: {},
				lastSaveTime,
			}, () => {
				this.fetchAccountSystem(activeSystemUUID);
				this.fetchAccountTemplates();

				app.setState({
					loading: false,
				});
			});

			return;
		}

		// Step GROUP (Group) uses Save functionality from Content Groups.
		if (step === GROUP) {
			contentGroupsSubmitForm(cbKey, cbVal);
			return;
		}

		// Rebuild the systemMap after a successful network response.
		app.setState({
			loading: true,
		});

		const fallbackPayload = buildDDPayload(pendingUpdates);
		if (fallbackPayload) {
			await saveData(fallbackPayload);
		}
		this.setState({
			pendingUpdates: {},
		}, () => {
			this.fetchAccountTemplates();
		});
	}

	stateCallback(key, val) {
		if (typeof key !== undefined && typeof val !== undefined) {
			this.setState({
				[key]: val,
			});
		}
	}

	handleDontSave(modalCallback = () => { }, cbKey, cbVal) {
		this.setState({
			pendingUpdates: {},
			assignmentMap:  {},
		}, () => {
			modalCallback(cbKey, cbVal);
		});
	}

	closeModal() {
		this.props.app.closeModal();
		this.setState({ pendingModalOpen: false }, () => {
			this.setSkeletonHasUpdated();
		});
	}

	openModal(stateKey, stateVal, templateBuilderStateCallback) {
		let stateCallBack = this.stateCallback.bind(this);
		if (templateBuilderStateCallback) {
			stateCallBack = templateBuilderStateCallback;
		}

		this.setState({ pendingModalOpen: true }, () => {
			this.props.app.openModal({
				modalSize:  1,
				showModal:  true,
				modalProps: {
					omitClose: true,
					title:     'Save Pending Changes?',
					jsx:       (
						<PendingChangesModal
							closeModal={this.closeModal.bind(this)}
							currentStep={this.state.step}
							handleDontSave={this.handleDontSave.bind(this)}
							handleSubmit={this.handleSubmit.bind(this)}
							stateCallback={stateCallBack}
							stateKey={stateKey}
							stateVal={stateVal}
						/>
					),
				},
			});
		});
	}

	hasBlockingColumnError() {
		return this.state.blockUpdatesEmptyColumn || this.state.blockUpdatesDataConflict;
	}

	alertBlockingColumnError() {
		alert((this.state.blockUpdatesEmptyColumn ? '\nThere is an empty column!' : '') +
			(this.state.blockUpdatesDataConflict ? '\nThere are columns with the same data selected!' : ''));
	}

	changeStep(newStep) {
		const {
			assignmentMap,
			pendingUpdates,
		} = this.state;

		if (this.hasBlockingColumnError()) {
			this.alertBlockingColumnError();
			return;
		}

		if (Object.keys(pendingUpdates).length > 0 || Object.keys(assignmentMap).length > 0) {
			this.openModal('step', newStep);
			return;
		}

		window.history.replaceState(null, '', `#step=${newStep}`);

		// Only re-fetch if data is stale (older than 30 seconds)
		const FRESHNESS_MS = 30000;
		const now = Date.now();
		const templatesStale = !this._lastTemplatesFetch || (now - this._lastTemplatesFetch) > FRESHNESS_MS;
		const globalStale = !this._lastGlobalTemplateFetch || (now - this._lastGlobalTemplateFetch) > FRESHNESS_MS;
		const systemsStale = !this._lastSystemsFetch || (now - this._lastSystemsFetch) > FRESHNESS_MS;

		// For SETUP (step 1), re-fetch core data if stale.
		if (newStep === SETUP) {
			if (globalStale) this.fetchGlobalTemplateData();
			if (templatesStale) this.fetchAccountTemplates();
		}
		// Step 2 (Assign) needs templates, global template data, and systems for template assignment
		if (newStep === ASSIGN) {
			if (templatesStale) this.fetchAccountTemplates();
			if (globalStale) this.fetchGlobalTemplateData();
			if (systemsStale) this.fetchAccountSystems();
		}

			// Step GROUP needs templates if stale
		if (newStep === GROUP) {
			if (templatesStale) this.fetchAccountTemplates();
		}

		// Step SYSTEMS (Systems) needs systems data
		if (newStep === SYSTEMS) {
			if (systemsStale) this.fetchAccountSystems();
		}

		this.setState({
			activeEntityKey:  '',
			activeSystemUUID: '',
			activeTLCUUID:    '',
			step:             newStep,
		});
	}

	handleStepFivePendingUpdates(newData) {
		const {
			settingMap,
		} = this.props;

		const {
			activeSystemUUID,
			systemMap,
		} = this.state;

		if (this.pendingUpdatesTimeout) {
			clearTimeout(this.pendingUpdatesTimeout);
		}
		if (this.state.pendingModalOpen) {
            return;
        }

		// Add a delay to wait for the user to finsish typing before we do a deep compare.
		this.pendingUpdatesTimeout = setTimeout(() => {
			const orgData = systemMap[`system.${activeSystemUUID}`];

			let pendingUpdates = {};
			let blockUpdatesEmptyColumn = false;
			let blockUpdatesDataConflict = false;
			let skeletonHasUpdated = false;

			// If orgData doesn't match what the newData that TemplateSkeleton has sent back, loop over newData to compare.
			if (orgData && JSON.stringify(orgData) !== JSON.stringify(newData)) {
				// Array to check if there are repeated data columns on a column group
				let column_group_columns = [];

				// Check for deleted m1Keys (components that exist in orgData but not in newData)
				// This handles the case where temporary ID components are completely removed
				Object.keys(orgData).forEach((m1Key) => {
					if (typeof newData[m1Key] === 'undefined' && m1Key.startsWith('component.')) {
						// Find the parent and mark the relationship as deleted
						const parentKey = orgData[m1Key]?.parent;
						if (parentKey) {
							pendingUpdates[`${parentKey}.${m1Key}`] = false;
						}
						// Also mark all the component's own data for deletion
						Object.entries(orgData[m1Key]).forEach(([ m2Key, m2Val, ]) => {
							if (m2Key !== 'type' && m2Key !== 'parent') {
								pendingUpdates[`${m1Key}.${m2Key}`] = false;
							}
						});
					}
				});

				Object.entries(newData).forEach(([ m1Key, newDataM2Maps, ]) => {
					// If orgData doesn't have the m1Key, we're adding a new component to the tree.
					if (typeof orgData[m1Key] === 'undefined') {
						// Loop over the new components children and add to pendingUpdates.

						Object.entries(newData[m1Key]).forEach(([ m2Key, m2Val, ]) => {
							// Skip type key and placeholder listing_column key
							if (m2Key === 'type') {
								return;
							}

							if (m2Key === 'listing_column.') {
								blockUpdatesEmptyColumn = true;
								return;
							}

							// If multiple column on a column group share the sama data, it shouldn't be possible to save.
							if (m1Key.includes('listing_column_group.') && m2Key.includes('listing_column.')) {
								// if (column_group_columns.includes(m2Key)) {
								// 	blockUpdatesDataConflict = true;
								// }
								column_group_columns.push(m2Key);
							}

							// Add the parent to child relation.
							if (m2Key === 'parent') {
								pendingUpdates[`${m2Val}.${m1Key}`] = true;
								return;
							}

							// COL (or null/undefined values)
							if (typeof m2Val !== 'object' || m2Val === null) {
								pendingUpdates[`${m1Key}.${m2Key}`] = m2Val;
								return;
							}

							// REL
							if (Object.keys(m2Val).length === 0) {
								pendingUpdates[`${m1Key}.${m2Key}`] = true;
								return;
							}

							// ATTR
							Object.entries(m2Val).forEach(([ attrKey, attrVal, ]) => {
								pendingUpdates[`${m1Key}.${m2Key}.${attrKey}`] = attrVal;
							});
						});


						return;
					}

					// Check for COL and REL changes.
					if (JSON.stringify(orgData[m1Key]) !== JSON.stringify(newData[m1Key])) {
						Object.keys(orgData[m1Key]).forEach((m2Key) => {
							// If there was orgData[m1Key][m2Key], compare the value to newData[m1Key][m2Key] and update.
							if (typeof orgData[m1Key][m2Key] !== 'undefined' && JSON.stringify(orgData[m1Key][m2Key]) !== JSON.stringify(newData[m1Key][m2Key])) {
								pendingUpdates[`${m1Key}.${m2Key}`] = newData[m1Key][m2Key];
							}

							// If newData[m1Key] doesn't have the m2Key from orgData[m1Key], we're deleting a component from the tree.
							if (typeof newData[m1Key][m2Key] === 'undefined') {
								pendingUpdates[`${m1Key}.${m2Key}`] = false;
							}
						});

						Object.keys(newData[m1Key]).forEach((m2Key) => {
							if (m2Key === 'custom_content_sidebar_label') {
								if (typeof orgData[m1Key][m2Key] === 'undefined') {
									orgData[m1Key][m2Key] = '';
									pendingUpdates[`${m1Key}.${m2Key}`] = newData[m1Key][m2Key];
								}
							}

							// If orgData[m1Key] doesn't have the m2Key from newData[m1Key], add the new relation and check for attrs.
							if (typeof orgData[m1Key][m2Key] === 'undefined') {
								pendingUpdates[`${m1Key}.${m2Key}`] = true;

								if (typeof newData[m1Key][m2Key] === 'object') {
									Object.entries(newData[m1Key][m2Key]).forEach(([ attr, val, ]) => {
										pendingUpdates[`${m1Key}.${m2Key}.${attr}`] = val;
									});
								}
							}
						});
					}

					// Check for setting changes.
					Object.entries(newDataM2Maps).forEach(([ m2Key, m2Val, ]) => {
						// If multiple column on a column group share the sama data, it shouldn't be possible to save.
						if (m1Key.includes('listing_column_group.') && m2Key.includes('listing_column.')) {
							// if (column_group_columns.includes(m2Key)) {
							// 	blockUpdatesDataConflict = true;
							// }
							column_group_columns.push(m2Key);
						}

						const {
							attr,
						} = parseEntityName(`${m1Key}.${m2Key}`);
						if (typeof attr === 'undefined') {
							return;
						}

						if (JSON.stringify(orgData[m1Key][m2Key]) !== JSON.stringify(m2Val)) {
							// Only 1 key/val in m2Val.
							Object.entries(m2Val).forEach(([ attr, val, ]) => {
								pendingUpdates[`${m1Key}.${m2Key}.${attr}`] = val;
							});
						}

						// Find TLC and assign is_custom to true.
						if (m2Key === 'type' && m2Val === 'top_level_condor') {
							pendingUpdates[`${m1Key}.setting.${settingMap['is_custom'].uuid}.bool`] = true;
						}
					});
				});
			}

			let condorRenderNameM1Key = `setting.${this.props.settingMap['condor_render_name'].uuid}`;
			let customContentSidebarLabelM1Key = `setting.${this.props.settingMap['custom_content_sidebar_label'].uuid}`;

			for (const key of Object.keys(pendingUpdates)) {
				if (key.endsWith(condorRenderNameM1Key) || key.endsWith(customContentSidebarLabelM1Key)) {
					Reflect.deleteProperty(pendingUpdates, key);
				}

				if (key.endsWith(condorRenderNameM1Key + '.string') || key.endsWith(customContentSidebarLabelM1Key + '.string')) {
					Reflect.deleteProperty(pendingUpdates, key);
				}
			}

			for (const [ key, val, ] of Object.entries(pendingUpdates)) {
				// m1.*.visual_content_loop_*.*
				if (key.includes('visual_content_loop') && !key.includes('.settings.')) {
					Reflect.deleteProperty(pendingUpdates, key);
				}

				if (key.includes('.componentDisabled')) {
					Reflect.deleteProperty(pendingUpdates, key);
				}
			}

			// Trigger Save button.
			this.setState({
				pendingUpdates,
				blockUpdatesEmptyColumn,
				blockUpdatesDataConflict,
				skeletonHasUpdated,
			});
		}, 500);
	}

	setActiveSystem(systemUUID) {
		const {
			systemMap,
		} = this.state;

		if (this.hasBlockingColumnError()) {
			this.alertBlockingColumnError();
			return;
		}

		if (Object.keys(this.state.pendingUpdates).length) {
			this.openModal('activeSystemUUID', systemUUID);
			return;
		}

		let tlcUUID = '';

		Object.entries(systemMap[`system.${systemUUID}`]).forEach(([ key, val, ]) => {
			if (key.startsWith('component.') && val.description === 'top_level_condor') {
				tlcUUID = key;
			}
		});

		this.setSkeletonHasUpdated();
		this.setState({
			activeSystemUUID: systemUUID,
			activeTLCUUID:    tlcUUID,
		});
	}

	turnOffRebuildBundleMap() {
		this.setState({
			shouldRebuildBundleMap: false,
		});
	}

	turnOnRebuildBundleMap() {
		this.setState({
			shouldRebuildBundleMap: true,
		});
	}

	renderSystems() {
		const {
			fd,
		} = this.props;

		const {
			activeEntityKey,
			activeSystemUUID,
			systemGroupMap,
			systemMap,
		} = this.state;

		let projectNumber = '';

		return (
			Object.keys(systemGroupMap).map((entityKey) => {
				const {
					m1ID: systemGroupUUID,
				} = parseEntityName(entityKey);
				const systemGroupName = getFDValue(fd, `system_group.${systemGroupUUID}.display_name`);
				return {
					systemGroupUUID,
					systemGroupName,
				};
			})
				.sort((a, b) => {
					if (!a.systemGroupName || !b.systemGroupName) {
						return 0;
					}

					if (a.systemGroupName.toLowerCase() > b.systemGroupName.toLowerCase()) {
						return 1;
					}
					return -1;
				})
				.map(({
					systemGroupUUID,
					systemGroupName,
				}) => {
					let systemGroupM1Key = `system_group.${systemGroupUUID}`;
					projectNumber = this.props.fd[systemGroupM1Key]?.project_number || '';

					return (
						<div
							className={style.systemGroupLabel}
							key={systemGroupUUID}
						>
							<div className={style.systemGroupText}>{systemGroupName} <span className={style.projectNumber}>{projectNumber && `- ${projectNumber}`}</span></div>
							{matchFDKeys(fd, `system_group.${systemGroupUUID}.system.*`).map((nestedEntityKey) => {
								const {
									m2ID: systemUUID,
								} = parseEntityName(nestedEntityKey);
								const systemName = getFDValue(fd, `system.${systemUUID}.display_name`);
								const directoryNumber = getFDValue(fd, `system.${systemUUID}.directory_number`);

								return {
									systemUUID,
									systemName,
									directoryNumber,
								};
							})
								.sort((a, b) => {
									if (a.systemName.toLowerCase() > b.systemName.toLowerCase()) {
										return 1;
									}
									return -1;
								})
								.map(({
									systemUUID, systemName, directoryNumber,
								}) => {
									let activeSystemLabel = '';
									if (systemUUID === activeSystemUUID) {
										activeSystemLabel = style.activeSystemLabel;
									}

									let contentGroupIcon = '';
									if (systemMap[`system.${systemUUID}`]?.[activeEntityKey]) {
										contentGroupIcon = <CustomIcon icon='object-group' color='green' />;
									}

									let disabled = '';
									const systemToComponentList = matchFDKeys(fd, `system.${systemUUID}.component.*`);

									// Disable system if it doesn't have a child TLC. system.*.component.* can be false from Step GROUP.
									let systemToComponentRel = false;
									systemToComponentList.forEach((systemToComponentKey) => {
										let val = getFDValue(fd, systemToComponentKey);
										if (val) {
											systemToComponentRel = true;
										}
									});

									if (!systemToComponentRel) {
										disabled = style.disabledSystemLabel;
									}

									let tlcUUID = '';

									Object.entries(systemMap[`system.${systemUUID}`]).forEach(([ key, val, ]) => {
										if (key.startsWith('component.') && val.description === 'top_level_condor') {
											tlcUUID = key.replace('component.', '');
										}
									});

									let hasMobile = false;

									if (fd?.['component.' + tlcUUID]) {
										hasMobile = Object.keys(fd['component.' + tlcUUID]).findIndex((m2Key) => {
											if (fd[m2Key]?.description === 'mobile') {
												return true;
											}
										}) !== -1;
									}

									// `data-system-name` is exposed so tests can target one system exactly:
									// the visible label appends the directory number, so text matching
									// can't distinguish "touch3 dk" from "touch3 dk 2".
									return (
										<div
											data-test-id="systemLabel"
											data-disabled={disabled ? 'true' : 'false'}
											data-system-name={systemName}
											id={`sp_system_${systemUUID}`}
											className={`${style.systemLabel} ${activeSystemLabel} ${disabled}`}
											key={systemUUID}
											onClick={(e) => {
												e.preventDefault();
												if (!disabled) {
													this.setActiveSystem(systemUUID);
												}
											}}
										>
											<div
												data-test-id="systemLabelContainer"
												className={style.systemLabelContainer}
											>
												<span className={style.systemIcon}>{contentGroupIcon}</span>
												<span>{systemName} <span className={style.directoryNumber}>{directoryNumber && `- ${directoryNumber}`}</span></span>
											</div>
											<div
												className={style.condorIconsContainer}
											>
												<div
													alt='Open Preview'
													data-test-id="systemPreviewIcon"
													className={style.condorPreviewIcon}
													onClick={(e) => {
														e.stopPropagation();

														if (!tlcUUID) {
															return;
														}

														const previewHost = window._getEnv('PREVIEW_URL');
														window.open(`${previewHost}/?condor_component=${tlcUUID}`, '_blank');
													}}
												>
													<CustomIcon icon='eye' color={tlcUUID ? '#4BA046' : '#4B4B4B'} />
												</div>
												<div
													alt='Open Mobile App'
													data-test-id="systemMobilePreviewIcon"
													className={style.condorMobileIcon}
													onClick={(e) => {
														e.stopPropagation();

														if (!tlcUUID || !hasMobile) {
															return;
														}

														const mobileHost = window._getEnv('CONDOR_MOBILE_URL');
														window.open(`${mobileHost}/?id=${tlcUUID}`, '_blank');
													}}
												>
													<CustomIcon icon='mobile-alt' color={(tlcUUID && hasMobile) ? '#4BA046' : '#aeaeae'} />
												</div>
											</div>
										</div>
									);
								})}
						</div>
					);
				})
		);
	}

	renderStepFive() {
		const {
			app,
			accountUUID,
			settingMap,
			fd,
		} = this.props;

		const { listingConfigMap, listingConfigMapLoaded, } = this.context;

		const {
			activeSystemUUID,
			activeTLCUUID,
			systemMap,
			systemGroupMap,
		} = this.state;

		let existingFlatData = null;
		let settingsFlatData = null;
		let activeSystemFlatData = null;

		if (systemMap[`system.${activeSystemUUID}`]) {
			existingFlatData = systemMap[`system.${activeSystemUUID}`];
			settingsFlatData = Object.entries(fd).filter(([ key, ]) => {
				return key.startsWith('setting.');
			})
				.reduce((accum, [ key, val, ]) => {
					accum[key] = val;
					return accum;
				}, {});
			activeSystemFlatData = fd[`system.${activeSystemUUID}`];
		}

		if (activeSystemUUID && !existingFlatData) {
			return (
				<div className={style.stepFiveContainer}>
					<div className={style.stepFiveSystems}>
						<div className={style.systemsContainerHeader}>
							<div className={style.headerLabel}>SYSTEMS</div>
						</div>
						<div className={style.systemsScroller} data-test-id="systemsScroller">
							{this.renderSystems()}
						</div>
					</div>
					<div className={style.loading}>Loading...</div>
				</div>
			);
		}

		let activeSystemGroup;
		Object.entries(systemGroupMap).forEach(([ m1Key, m1Val, ]) => {
			Object.keys(m1Val).forEach((m2Key) => {
				if (m2Key === `system.${activeSystemUUID}`) {
					const activeSystemGroupUUID = m1Key.split('.')[1];
					activeSystemGroup = fd[`system_group.${activeSystemGroupUUID}`];
				}
			});
		});

		if (!listingConfigMapLoaded) {
			return (
				<div className={style.stepFiveContainer}>
					<div className={style.loading}>Loading...</div>
				</div>
			);
		}

		return (
			<div className={style.stepFiveContainer}>
				<div className={style.stepFiveSystems}>
					<div className={style.systemsContainerHeader}>
						<div className={style.headerLabel}>SYSTEMS</div>
					</div>
					<div className={style.systemsScroller} data-test-id="systemsScroller">
						{this.renderSystems()}
					</div>
				</div>
				{activeSystemUUID &&
					<TemplateSkeleton
						app={app}
						accountUUID={accountUUID}
						activeSystemUUID={activeSystemUUID}
						activeTLCUUID={activeTLCUUID}
						existingFlatData={existingFlatData}
						settingsFlatData={settingsFlatData}
						step={this.state.step}
						activeSystemFlatData={activeSystemFlatData}
						pendingUpdates={this.state.pendingUpdates}
						stateCallback={this.stateCallback.bind(this)}
						updateActiveEntityKey={this.stateCallback.bind(this)}
						updateSkeletonData={this.handleStepFivePendingUpdates.bind(this)}
						activeSystemGroup={activeSystemGroup}
						settingMap={settingMap}
						fd={fd}
						systemProgramming={this}
					/>
				}
			</div>
		);
	}

	renderHeaderButtons() {
		const {
			step,
		} = this.state;

		const btns = [
			'Setup',
			'Assign',
			'Group',
			'Systems',
		];

		return (
			<div className={style.headerBtnContainer}>
				{btns.map((btnText, i) => {
					let active = '';
					if (step === i + 1) {
						active = style.headerBtnActive;
					}

					return (
						<div
							key={i}
							id={`sp_step_${i + 1}`}
							className={`${style.headerBtn} ${active}`}
							data-test-id="systemProgrammingHeaderBtn"
							onClick={(e) => {
								e.preventDefault();
								this.changeStep(i + 1);
							}}
						>
							<span className={style.headerBtnNum}>{i + 1} </span>{btnText}
						</div>
					);
				})}
			</div>
		);
	}

	renderBody() {
		const {
			step,
		} = this.state;

		const {
			value,
			app,
			accountUUID,
			fd,
			settingMap,
			pendingChanges,
		} = this.props;

		switch (step) {
		case SETUP:
		case ASSIGN:
			return (
				<TemplateBuilder
					app={app}
					settingMap={settingMap}
					fd={fd}
					accountUUID={accountUUID}
					assignmentMap={this.state.assignmentMap}
					globalTemplateData={this.state.globalTemplateData}
					openModal={this.openModal.bind(this)}
					pendingUpdates={this.state.pendingUpdates}
					stateCallback={this.stateCallback.bind(this)}
					makeMaps={this.makeMaps.bind(this)}
					step={step}
					value={value}
					setSkeletonHasUpdated={this.setSkeletonHasUpdated.bind(this)}
					refreshGlobalTemplateData={this.fetchGlobalTemplateData.bind(this)}
				/>
			);
		case GROUP:
			return (
				<ContentGroups
					app={app}
					fd={fd}
					pendingChanges={pendingChanges}
					settingMap={settingMap}
					systemProgramming={this}

					accountUUID={accountUUID}
					buildSystemMap={this.makeMaps.bind(this)}
					systemProgrammingPendingUpdates={this.state.pendingUpdates}
					stateCallback={this.stateCallback.bind(this)}
					step={step}
					value={value}
					shouldRebuildBundleMap={this.state.shouldRebuildBundleMap}
					turnOffRebuildBundleMap={this.turnOffRebuildBundleMap.bind(this)}
					turnOnRebuildBundleMap={this.turnOnRebuildBundleMap.bind(this)}
					refreshAccount={this.refreshAccount.bind(this)}
				/>
			);
		case SYSTEMS:
			return this.renderStepFive();
		default:
			return;
		}
	}

	renderSystemProgramming() {
		const {
			pendingUpdates,
			step,
			assignmentMap,
		} = this.state;

		let title = '';
		switch (step) {
		case SETUP:
			title = 'CONFIGURATION';
			break;
		case ASSIGN:
			title = 'SYSTEM PROGRAMMING';
			break;
		case GROUP:
			title = 'CONTENT GROUPS';
			break;
		case SYSTEMS:
			title = 'SYSTEM COMPONENTS';
			break;
		default:
			return;
		}

		let disabled = true;
		let saveButtonClassName = 'button button-save button-disabled';
		if (Object.keys(pendingUpdates).length > 0) {
			disabled = false;
			saveButtonClassName = 'button button-save';
		}

		// for step ASSIGN (Assign page - assignmentMap)
		if (Object.keys(assignmentMap).length > 0) {
			disabled = false;
			saveButtonClassName = 'button button-save';
		}

		return (
			<div className='systems-page'>
				<div className={style.title}>
					<h2>{title}</h2>
					<button
						className={saveButtonClassName}
						disabled={disabled}
						onClick={(e) => {
							e.preventDefault();
							if (this.hasBlockingColumnError()) {
								this.alertBlockingColumnError();
							} else {
								this.handleSubmit();
								this.setSkeletonHasUpdated();
							}
						}}
					>
						<CustomIcon icon='save' />Save
					</button>
				</div>

				{this.renderHeaderButtons()}

				{this.renderBody()}
			</div>
		);
	}

	render() {
		return (
			<React.Fragment>
				{this.renderSystemProgramming()}
				<N8nChat metadata={this.buildN8nMetadata()} />
			</React.Fragment>
		);
	}
}

function PendingChangesModal(props) {
	return (
		<React.Fragment>
			<div className={style.buttonContainer}>
				<button
					onClick={(e) => {
						e.preventDefault();
						props.closeModal();
					}}
					className='button button-outlined'
				><CustomIcon icon='undo' />Go Back</button>

				<button
					onClick={(e) => {
						e.preventDefault();
						props.handleDontSave(props.stateCallback, props.stateKey, props.stateVal);
						props.closeModal();
					}}
					className='button'
				><CustomIcon icon='times' />{`Don't Save`}</button>

				<button
					onClick={(e) => {
						e.preventDefault();
						props.handleSubmit(props.stateCallback, props.stateKey, props.stateVal);

						// Step GROUP has a custom save modal, don't close the modal on step GROUP.
						if (props.currentStep !== GROUP) {
							props.closeModal();
						}
					}}
					className='button'
				><CustomIcon icon='save' />Save</button>
			</div>
		</React.Fragment>
	);
}

if (!window.CrowFormField) {
	window.CrowFormField = {};
}
window.CrowFormField['custom.vulture.system.programming'] = SystemProgramming;
export default SystemProgramming;